import { io } from 'socket.io-client';

const SOCKET_URL = process.env.REACT_APP_SOCKET_URL || 'http://localhost:5000';

function toErrText(x) {
  if (!x) return 'Неизвестная ошибка';
  if (typeof x === 'string') return x;
  if (x.message) return String(x.message);
  if (x.error) return typeof x.error === 'string' ? x.error : JSON.stringify(x.error);
  if (x.reason) return String(x.reason);
  try { return JSON.stringify(x); } catch { return String(x); }
}

function dispatchConn(detail) {
  if (typeof window === 'undefined') return;

  // Новый канал Хачапури.
  window.dispatchEvent(new CustomEvent('khachapuri-conn', { detail }));

  // Совместимость с копией UI длинных нард: BoardDisplay уже слушает long-conn.
  // Namespace сокета при этом ОТДЕЛЬНЫЙ (/khachapuri), поэтому длинные игры не затрагиваются.
  window.dispatchEvent(new CustomEvent('long-conn', { detail }));
}

function getSnapRoomFromLocalStorage() {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return null;

    const storage = window.localStorage;
    const prefix = 'khachapuri:snap:';
    const keys = [];

    for (let i = 0; i < storage.length; i++) {
      const key = storage.key(i);
      if (key && key.startsWith(prefix)) keys.push(key);
    }

    if (!keys.length) return null;

    const snapKey = keys[keys.length - 1];
    const raw = storage.getItem(snapKey);
    if (!raw) return null;

    const snap = JSON.parse(raw);
    const tableId = Number(
      snap.tableId ??
      snap.table_id ??
      snap.khachapuri_table_id ??
      snap.long_table_id
    );

    if (!Number.isFinite(tableId)) return null;

    const playerName =
      storage.getItem('username') ||
      snap.p1 ||
      snap.p2 ||
      null;

    if (!playerName) return null;
    return { tableId, playerName, isSpectator: false };
  } catch (e) {
    console.warn('[khachapuriSocket] getSnapRoomFromLocalStorage:', e);
    return null;
  }
}

function readSocketAuth() {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return {};
    const token = window.localStorage.getItem('token');
    return token ? { token } : {};
  } catch (e) {
    return {};
  }
}

const khachapuriSocket = io(`${SOCKET_URL}/khachapuri`, {
  autoConnect: false,
  auth: readSocketAuth(),
  transports: ['websocket'],
  withCredentials: false,
  reconnection: true,
  reconnectionAttempts: Infinity,
  reconnectionDelay: 500,
  reconnectionDelayMax: 5000,
  timeout: 20000,
});

let currentKhachapuriRoom = null;

function refreshAuthFromLocalStorage() {
  try {
    khachapuriSocket.auth = readSocketAuth();
  } catch (e) {
    console.warn('[khachapuriSocket] auth read error:', e);
  }
}

function loadKhachapuriRoomFromCache() {
  if (currentKhachapuriRoom) return currentKhachapuriRoom;

  try {
    const raw = localStorage.getItem('khachapuri:lastRoom') || 'null';
    const saved = JSON.parse(raw);
    if (saved?.tableId && saved?.playerName) {
      currentKhachapuriRoom = saved;
    }
  } catch (e) {
    console.warn('[khachapuriSocket] lastRoom read error:', e);
  }

  return currentKhachapuriRoom;
}

function rejoinIfNeeded() {
  try {
    let info = loadKhachapuriRoomFromCache() || null;
    const snapRoom = getSnapRoomFromLocalStorage();

    if (snapRoom) {
      info = info
        ? {
            ...info,
            tableId: snapRoom.tableId,
            playerName: info.playerName || snapRoom.playerName,
          }
        : snapRoom;
    }

    if (!info?.tableId || !info?.playerName) return;

    currentKhachapuriRoom = info;

    khachapuriSocket.emit('joinKhachapuriTable', {
      tableId: Number(info.tableId),
      playerUsername: info.playerName,
      isSpectator: Boolean(info.isSpectator),
    });
  } catch (e) {
    console.warn('[khachapuriSocket] rejoinIfNeeded:', e);
  }
}

export function ensureKhachapuriSocketAuthAndConnect() {
  refreshAuthFromLocalStorage();
  if (!khachapuriSocket.connected) khachapuriSocket.connect();
}

export function rememberKhachapuriRoom(tableId, playerName, isSpectator = false) {
  currentKhachapuriRoom = {
    tableId: Number(tableId),
    playerName,
    isSpectator: Boolean(isSpectator),
  };

  try {
    localStorage.setItem(
      'khachapuri:lastRoom',
      JSON.stringify(currentKhachapuriRoom)
    );
  } catch (e) {
    console.warn('[khachapuriSocket] room cache save error:', e);
  }
}

export function clearKhachapuriRoom() {
  currentKhachapuriRoom = null;

  try {
    localStorage.removeItem('khachapuri:lastRoom');

    if (typeof window !== 'undefined' && window.localStorage) {
      const storage = window.localStorage;
      const prefix = 'khachapuri:snap:';
      const toDelete = [];

      for (let i = 0; i < storage.length; i++) {
        const key = storage.key(i);
        if (key && key.startsWith(prefix)) toDelete.push(key);
      }

      toDelete.forEach((key) => storage.removeItem(key));
    }
  } catch (e) {
    console.warn('[khachapuriSocket] room cache clear error:', e);
  }
}

// Алиасы нужны только потому, что визуальный компонент Хачапури — безопасная копия
// уже работающего Long UI. Сам сокет остаётся /khachapuri.
export const ensureLongSocketAuthAndConnect = ensureKhachapuriSocketAuthAndConnect;
export const rememberLongRoom = rememberKhachapuriRoom;
export const clearLongRoom = clearKhachapuriRoom;

khachapuriSocket.on('connect', () => {
  refreshAuthFromLocalStorage();
  rejoinIfNeeded();
  dispatchConn({ state: 'online' });
});

khachapuriSocket.on('disconnect', (reason) => {
  dispatchConn({ state: 'offline', reason: toErrText(reason) });
});

khachapuriSocket.on('connect_error', (err) => {
  const message = String(err?.message || '');

  if (message.includes('SESSION_EXPIRED')) {
    try {
      localStorage.removeItem('token');
      localStorage.removeItem('username');
    } catch (e) {}

    if (typeof window !== 'undefined') window.location.href = '/login';
    return;
  }

  console.error('[khachapuriSocket] connect_error:', err);
  dispatchConn({ state: 'error', message: toErrText(err) });
});

khachapuriSocket.io.on('reconnect_attempt', (attempt) => {
  refreshAuthFromLocalStorage();
  dispatchConn({ state: 'reconnecting', attempt });
});

khachapuriSocket.io.on('reconnect', (attempt) => {
  dispatchConn({ state: 'online', recovered: true, attempt });
});

khachapuriSocket.on('khachapuri_forceActiveTable', (payload) => {
  const tableId = Number(payload?.tableId ?? payload?.activeTableId);
  if (!Number.isInteger(tableId) || tableId <= 0) return;

  const playerName = localStorage.getItem('username') || '';
  clearKhachapuriRoom();
  if (playerName) rememberKhachapuriRoom(tableId, playerName, false);

  try {
    localStorage.setItem('gameType', 'khachapuri');
  } catch (e) {}

  const target = `/khachapuriGame/${tableId}`;
  if (window.location.pathname !== target) window.location.replace(target);
});

export default khachapuriSocket;
